import { Icon } from '@iconify/react'
import { useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Card } from '@/components/ui/Card'
import { Input, SearchInput, Select } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { PROJECT_STATUS_LABELS, PROVINCE_NAMES, PROCUREMENT_METHOD_LABELS } from '@/lib/constants'
import { cn } from '@/lib/utils'

interface TenderFiltersProps {
  onFiltersChange: (filters: FilterValues) => void
  className?: string
}

export interface FilterValues {
  search?: string
  status?: string[]
  province?: string
  procurementMethod?: string[]
  minBudget?: string
  maxBudget?: string
  dateFrom?: string
  dateTo?: string
}

type FilterFormValues = FilterValues & {
  methodExpanded: boolean
}

const STATUS_OPTIONS = Object.entries(PROJECT_STATUS_LABELS).map(([value, labels]) => ({
  value,
  label: labels.simple,
}))

const METHOD_OPTIONS = Object.entries(PROCUREMENT_METHOD_LABELS).map(([value, label]) => ({
  value,
  label,
}))

const METHOD_OPTION_LABELS = METHOD_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[option.value] = option.label
  return acc
}, {})

const METHOD_LABELS: Record<string, string> = METHOD_OPTION_LABELS

const PROVINCE_OPTIONS = Object.entries(PROVINCE_NAMES).map(([value, label]) => ({
  value,
  label,
}))

export function TenderFilters({ onFiltersChange, className }: TenderFiltersProps) {
  const [searchParams, setSearchParams] = useSearchParams()

  const { register, watch, setValue, reset, handleSubmit } = useForm<FilterFormValues>({
    defaultValues: {
      search: searchParams.get('q') || '',
      status: searchParams.getAll('status'),
      province: searchParams.get('province') || '',
      procurementMethod: searchParams.getAll('method'),
      minBudget: searchParams.get('minBudget') || '',
      maxBudget: searchParams.get('maxBudget') || '',
      dateFrom: searchParams.get('dateFrom') || '',
      dateTo: searchParams.get('dateTo') || '',
      methodExpanded: false,
    },
  })

  const watchedStatus = watch('status') || []
  const watchedMethod = watch('procurementMethod') || []
  const methodExpanded = watch('methodExpanded') ?? false
  const selectedMethods = watchedMethod.map((method) => ({
    value: method,
    label: METHOD_LABELS[method] || method,
  }))

  const toggleStatus = (status: string) => {
    const current = watchedStatus
    const updated = current.includes(status)
      ? current.filter((s) => s !== status)
      : [...current, status]
    setValue('status', updated)
  }

  const toggleMethod = (method: string) => {
    const current = watchedMethod
    const updated = current.includes(method)
      ? current.filter((m) => m !== method)
      : [...current, method]
    setValue('procurementMethod', updated)
  }

  const onSubmit = (formData: FilterFormValues) => {
    const { methodExpanded: _methodExpanded, ...data } = formData
    // Update URL params
    const params = new URLSearchParams()
    if (data.search) params.set('q', data.search)
    if (data.province) params.set('province', data.province)
    if (data.minBudget) params.set('minBudget', data.minBudget)
    if (data.maxBudget) params.set('maxBudget', data.maxBudget)
    if (data.dateFrom) params.set('dateFrom', data.dateFrom)
    if (data.dateTo) params.set('dateTo', data.dateTo)
    data.status?.forEach((s) => params.append('status', s))
    data.procurementMethod?.forEach((m) => params.append('method', m))

    setSearchParams(params)
    onFiltersChange(data)
  }

  const clearFilters = () => {
    reset({
      search: '',
      status: [],
      province: '',
      procurementMethod: [],
      minBudget: '',
      maxBudget: '',
      dateFrom: '',
      dateTo: '',
      methodExpanded,
    })
    setSearchParams(new URLSearchParams())
    onFiltersChange({})
  }

  const activeFilterCount = [
    watchedStatus.length > 0,
    watchedMethod.length > 0,
    !!watch('province'),
    !!watch('minBudget') || !!watch('maxBudget'),
    !!watch('dateFrom') || !!watch('dateTo'),
  ].filter(Boolean).length

  return (
    <Card className={cn('p-4 font-display', className)}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold text-slate-900">ตัวกรอง</h3>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              ล้างทั้งหมด
            </button>
          )}
        </div>

        {/* Search */}
        <SearchInput
          placeholder="ค้นหาประกาศ..."
          {...register('search')}
        />

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            สถานะ
          </label>
          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleStatus(opt.value)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors',
                  watchedStatus.includes(opt.value)
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Province */}
        <Select
          label="จังหวัด"
          {...register('province')}
        >
          <option value="">ทุกจังหวัด</option>
          {PROVINCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>

        {/* Procurement Method */}
        <div>
          <div className="flex items-center justify-between gap-3">
            <label className="block text-sm font-medium text-slate-700">
              วิธีการจัดซื้อจัดจ้าง
            </label>
            <button
              type="button"
              onClick={() => setValue('methodExpanded', !methodExpanded)}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
              aria-expanded={methodExpanded}
              aria-controls="procurement-method-options"
            >
              <span>{methodExpanded ? 'ย่อรายการ' : 'แสดงทั้งหมด'}</span>
              <Icon
                icon="solar:alt-arrow-down-line-duotone"
                className={cn('w-4 h-4 transition-transform', methodExpanded && 'rotate-180')}
              />
            </button>
          </div>

          {selectedMethods.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedMethods.map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => toggleMethod(method.value)}
                  className="group"
                >
                  <Badge
                    variant="primary"
                    size="sm"
                    className="gap-1 pr-1.5"
                  >
                    {method.label}
                    <Icon
                      icon="solar:close-circle-line-duotone"
                      className="w-3.5 h-3.5 text-blue-600 group-hover:text-blue-700"
                    />
                  </Badge>
                </button>
              ))}
            </div>
          )}

          {methodExpanded && (
            <div id="procurement-method-options" className="flex flex-wrap gap-2 mt-3">
              {METHOD_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleMethod(opt.value)}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors',
                    watchedMethod.includes(opt.value)
                      ? 'bg-blue-50 border-blue-200 text-blue-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Budget Range */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            ช่วงงบประมาณ (บาท)
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              placeholder="ต่ำสุด"
              {...register('minBudget')}
            />
            <Input
              type="number"
              placeholder="สูงสุด"
              {...register('maxBudget')}
            />
          </div>
        </div>

        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            วันประกาศ
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="date"
              {...register('dateFrom')}
            />
            <Input
              type="date"
              {...register('dateTo')}
            />
          </div>
        </div>

        {/* Submit */}
        <Button type="submit" className="w-full">
          <Icon icon="solar:magnifer-line-duotone" className="w-4 h-4 mr-2" />
          ใช้ตัวกรอง
        </Button>
      </form>
    </Card>
  )
}

// Compact filter bar for mobile/inline use
export function TenderFilterBar({
  onFiltersChange,
  className,
}: TenderFiltersProps) {
  const [searchParams] = useSearchParams()
  const activeFilters = [
    ...searchParams.getAll('status'),
    searchParams.get('province'),
    ...searchParams.getAll('method'),
  ].filter((value): value is string => Boolean(value))
  const resolveFilterLabel = (filter: string) =>
    PROJECT_STATUS_LABELS[filter as keyof typeof PROJECT_STATUS_LABELS]?.simple ||
    METHOD_LABELS[filter] ||
    PROVINCE_NAMES[filter] ||
    filter

  return (
    <div className={cn('flex items-center gap-2 overflow-x-auto pb-2', className)}>
      <SearchInput
        placeholder="ค้นหา..."
        className="w-48 flex-shrink-0"
        onChange={(e) => onFiltersChange({ search: e.target.value })}
        defaultValue={searchParams.get('q') || ''}
      />

      {activeFilters.map((filter) => (
        <Badge key={filter} variant="secondary" className="flex-shrink-0">
          {resolveFilterLabel(filter)}
          <button className="ml-1 hover:text-slate-700">
            <Icon icon="solar:close-circle-line-duotone" className="w-3.5 h-3.5" />
          </button>
        </Badge>
      ))}
    </div>
  )
}
